import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDarkMode } from '../../../context/DarkModeContext';
import { useAchievements, Achievement } from '../../../hooks/useAchievements';
import { 
  IoCheckmarkCircleOutline,
  IoEllipseOutline,
  IoStarOutline,
  IoStar,
  IoTrophyOutline,
  IoInformationCircleOutline
} from 'react-icons/io5';

interface AchievementSelectorProps {
  focusId: string;
  onSelectionChange: (achievements: Achievement[]) => void;
  selectedAchievements: Achievement[];
}

const AchievementSelector: React.FC<AchievementSelectorProps> = ({
  focusId,
  onSelectionChange,
  selectedAchievements
}) => {
  const { darkMode } = useDarkMode();
  const { achievements, featuredAchievements, loading } = useAchievements(focusId);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    new Set(selectedAchievements.map(a => a.id))
  );

  // Style classes
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const cardBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const selectedBg = darkMode ? 'bg-purple-900/20' : 'bg-purple-50';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const hoverBg = darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50';

  useEffect(() => {
    // Auto-select featured achievements on mount
    if (featuredAchievements.length > 0 && selectedIds.size === 0) {
      const featuredIds = new Set(featuredAchievements.map(a => a.id));
      setSelectedIds(featuredIds);
      onSelectionChange(featuredAchievements);
    }
  }, [featuredAchievements]);

  const toggleSelection = (achievement: Achievement) => {
    const newSelectedIds = new Set(selectedIds);
    
    if (newSelectedIds.has(achievement.id)) {
      newSelectedIds.delete(achievement.id);
    } else {
      // Limit to 5 selections
      if (newSelectedIds.size >= 5) {
        return;
      }
      newSelectedIds.add(achievement.id);
    }
    
    setSelectedIds(newSelectedIds);
    const selectedAchievements = achievements.filter(a => newSelectedIds.has(a.id));
    onSelectionChange(selectedAchievements);
  };

  const getCategoryBadge = (category?: string) => {
    const categoryColors = {
      technical: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      leadership: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
      collaboration: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      innovation: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
      impact: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
      growth: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
    };
    
    return categoryColors[category as keyof typeof categoryColors] || categoryColors.technical;
  };

  const getImpactStars = (score?: number) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i}>
          {i <= (score || 3) ? (
            <IoStar className="w-4 h-4 text-yellow-500" />
          ) : (
            <IoStarOutline className="w-4 h-4 text-gray-400" />
          )}
        </span>
      );
    }
    return stars;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Instructions */}
      <div className={`p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800`}>
        <div className="flex gap-3">
          <IoInformationCircleOutline className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className={`font-medium text-blue-800 dark:text-blue-200 mb-1`}>
              Select Your Top Achievements
            </h3>
            <p className={`text-sm text-blue-700 dark:text-blue-300`}>
              Choose up to 5 achievements that best demonstrate your impact and growth. 
              Featured achievements are pre-selected but you can adjust the selection.
            </p>
          </div>
        </div>
      </div>

      {/* Selection Summary */}
      <div className={`flex items-center justify-between p-4 rounded-lg ${cardBg} border ${borderColor}`}>
        <div>
          <h4 className={`font-medium ${textColor}`}>
            Selected: {selectedIds.size} / 5 achievements
          </h4>
          <p className={`text-sm ${mutedTextColor} mt-1`}>
            These will be highlighted in your performance review
          </p>
        </div>
        <div className="flex items-center gap-2">
          {[1, 2, 3, 4, 5].map(i => (
            <div
              key={i}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                i <= selectedIds.size
                  ? 'bg-purple-600 text-white'
                  : darkMode
                  ? 'bg-gray-700 text-gray-500'
                  : 'bg-gray-200 text-gray-400'
              }`}
            >
              {i}
            </div>
          ))}
        </div>
      </div>

      {/* Achievements List */}
      <div className="space-y-3">
        {achievements.length === 0 ? (
          <div className={`text-center py-12 ${cardBg} border ${borderColor} rounded-lg`}>
            <IoTrophyOutline className={`w-12 h-12 ${mutedTextColor} mx-auto mb-4`} />
            <p className={textColor}>No achievements found</p>
            <p className={`text-sm ${mutedTextColor} mt-2`}>
              Create some achievements first to include them in your review
            </p>
          </div>
        ) : (
          <AnimatePresence>
            {achievements.map((achievement, index) => {
              const isSelected = selectedIds.has(achievement.id);
              const canSelect = isSelected || selectedIds.size < 5;
              
              return (
                <motion.div
                  key={achievement.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => canSelect && toggleSelection(achievement)}
                  className={`p-4 rounded-lg border ${borderColor} ${
                    isSelected ? selectedBg : cardBg
                  } ${canSelect ? `cursor-pointer ${hoverBg}` : 'cursor-not-allowed opacity-60'} transition-all`}
                >
                  <div className="flex items-start gap-4">
                    {/* Checkbox */}
                    <div className="mt-0.5">
                      {isSelected ? (
                        <IoCheckmarkCircleOutline className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                      ) : (
                        <IoEllipseOutline className={`w-6 h-6 ${mutedTextColor}`} />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className={`font-medium ${textColor} mb-1`}>
                            {achievement.title}
                          </h4>
                          <div className="flex items-center gap-3 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs ${getCategoryBadge(achievement.category)}`}>
                              {achievement.category}
                            </span>
                            <div className="flex items-center gap-1">
                              {getImpactStars(achievement.impact_score)}
                            </div>
                            {achievement.is_featured && (
                              <div className="flex items-center gap-1 text-yellow-600 dark:text-yellow-400">
                                <IoTrophyOutline className="w-4 h-4" />
                                <span className="text-xs">Featured</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <span className={`text-sm ${mutedTextColor}`}>
                          {new Date(achievement.achievement_date).toLocaleDateString('en-US', {
                            month: 'short',
                            year: 'numeric'
                          })}
                        </span>
                      </div>

                      {/* STAR Summary */}
                      <div className={`text-sm ${mutedTextColor} space-y-1`}>
                        <p className="line-clamp-2">
                          <span className="font-medium">Result:</span> {achievement.result}
                        </p>
                      </div>

                      {/* Key Skills */}
                      {achievement.key_skills && achievement.key_skills.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {achievement.key_skills.slice(0, 3).map((skill, idx) => (
                            <span
                              key={idx}
                              className={`px-2 py-0.5 text-xs rounded-full ${
                                darkMode ? 'bg-gray-700' : 'bg-gray-100'
                              } ${mutedTextColor}`}
                            >
                              {skill}
                            </span>
                          ))}
                          {achievement.key_skills.length > 3 && (
                            <span className={`text-xs ${mutedTextColor}`}>
                              +{achievement.key_skills.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}
      </div>

      {/* Tips */}
      <div className={`p-4 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800`}>
        <h4 className={`font-medium text-purple-800 dark:text-purple-200 mb-2`}>
          Selection Tips
        </h4>
        <ul className={`text-sm text-purple-700 dark:text-purple-300 space-y-1`}>
          <li>• Choose achievements that align with your role's key competencies</li>
          <li>• Include a mix of categories to show well-rounded performance</li>
          <li>• Prioritize achievements with measurable business impact</li>
          <li>• Consider recency - focus on the current review period</li>
        </ul>
      </div>
    </div>
  );
};

export default AchievementSelector;